import CanvasUtils from '@/utils/CanvasUtils'

/**
 * 星盘渲染器类
 * 封装所有星盘绘制功能，通过实例化使用
 */
export default class AstrolabeRenderer {
  /**
   * 构造函数
   * @param {CanvasRenderingContext2D} ctx - 画布上下文
   * @param {Object} center - 中心点坐标 {x, y}
   * @param {number} radius - 基准半径
   * @param {Object} [styles={}] - 样式配置
   * @param {number} [zeroAngle=180] - 零度基准角度
   */
  constructor (ctx, center, radius, styles = {}, zeroAngle = 180) {
    this.ctx = ctx
    this.center = center
    this.radius = radius
    this.styles = styles
    this.zeroAngle = zeroAngle
    // 所有绘制均使用逆时针方向
    // 地平盘的方位角数据通过 AstrolabeDrawer.convertAzimuthToDrawAngle() 转换为逆时针绘制角度

    this.styles = {
      baseCircle: {
        strokeStyle: '#34495e',
        lineWidth: 1
      }
    }
  }

  /**
   * 更新渲染器配置
   * @param {Object} config - 新的配置参数
   * @param {CanvasRenderingContext2D} [config.ctx] - 新的画布上下文
   * @param {Object} [config.center] - 新的中心点坐标
   * @param {number} [config.radius] - 新的半径
   * @param {Object} [config.styles] - 新的样式配置
   * @param {number} [config.zeroAngle] - 新的零度基准角度
   */
  updateConfig (config) {
    if (config.ctx) this.ctx = config.ctx
    if (config.center) this.center = config.center
    if (config.radius) this.radius = config.radius
    if (config.styles) this.styles = config.styles
    if (config.zeroAngle !== undefined) this.zeroAngle = config.zeroAngle
  }

  /**
   * 绘制基础圆形
   * @param {string} [strokeStyle] - 描边颜色
   * @param {number} [lineWidth] - 线宽
   */
  drawBaseCircle (strokeStyle, lineWidth) {
    const style = this.styles.baseCircle || {}
    this.ctx.beginPath()
    this.ctx.arc(this.center.x, this.center.y, this.radius * 0.9, 0, Math.PI * 2)
    this.ctx.strokeStyle = strokeStyle || style.strokeStyle || '#34495e'
    this.ctx.lineWidth = lineWidth || style.lineWidth || 1
    // 填充扇形
    this.ctx.fillStyle = '#fff'
    this.ctx.fill()
    this.ctx.stroke()
  }

  /**
   * 绘制扇形环
   * 【说明】
   * - 始终使用逆时针绘制
   * - 地平盘的方位角数据在传入前已通过 convertAzimuthToDrawAngle() 转换为逆时针角度
   *
   * @param {number} startAngle - 起始角度
   * @param {number} endAngle - 结束角度
   * @param {number} [innerRadiusRatio] - 内半径比例
   * @param {number} [outerRadiusRatio] - 外半径比例
   * @param {string} [fillColor] - 填充颜色
   * @param {string} [strokeStyle] - 描边颜色
   * @param {number} [lineWidth] - 线宽
   * @param {boolean} [drawLines=true] - 是否绘制直边
   */
  drawSector (startAngle, endAngle, innerRadiusRatio, outerRadiusRatio, fillColor, strokeStyle, lineWidth, drawLines = true) {
    startAngle = CanvasUtils.normalizeAngle(startAngle)
    endAngle = CanvasUtils.normalizeAngle(endAngle)

    const innerRadius = this.radius * (innerRadiusRatio)
    const outerRadius = this.radius * (outerRadiusRatio)

    const startPoint = CanvasUtils.getPoint(startAngle, outerRadius, this.center, this.zeroAngle)
    const endPoint = CanvasUtils.getPoint(endAngle, innerRadius, this.center, this.zeroAngle)

    // 创建扇形路径，始终逆时针绘制
    this.ctx.beginPath()
    this.ctx.moveTo(startPoint.x, startPoint.y)
    this.ctx.arc(
      this.center.x, this.center.y, outerRadius,
      CanvasUtils.degToRad(startAngle, this.zeroAngle),
      CanvasUtils.degToRad(endAngle, this.zeroAngle),
      true
    )
    this.ctx.lineTo(endPoint.x, endPoint.y)
    this.ctx.arc(
      this.center.x, this.center.y, innerRadius,
      CanvasUtils.degToRad(endAngle, this.zeroAngle),
      CanvasUtils.degToRad(startAngle, this.zeroAngle),
      false
    )
    this.ctx.lineTo(startPoint.x, startPoint.y)
    this.ctx.closePath()

    // 填充扇形
    this.ctx.fillStyle = fillColor
    this.ctx.fill()

    // 创建描边路径，始终逆时针绘制
    this.ctx.beginPath()
    this.ctx.moveTo(startPoint.x, startPoint.y)
    this.ctx.arc(
      this.center.x, this.center.y, outerRadius,
      CanvasUtils.degToRad(startAngle, this.zeroAngle),
      CanvasUtils.degToRad(endAngle, this.zeroAngle),
      true
    )

    if (drawLines) {
      this.ctx.lineTo(endPoint.x, endPoint.y)
    } else {
      this.ctx.moveTo(endPoint.x, endPoint.y)
    }

    this.ctx.arc(
      this.center.x, this.center.y, innerRadius,
      CanvasUtils.degToRad(endAngle, this.zeroAngle),
      CanvasUtils.degToRad(startAngle, this.zeroAngle),
      false
    )

    if (drawLines) {
      this.ctx.lineTo(startPoint.x, startPoint.y)
    } else {
      this.ctx.moveTo(startPoint.x, startPoint.y)
    }

    this.ctx.strokeStyle = strokeStyle
    this.ctx.lineWidth = lineWidth
    this.ctx.stroke()
  }

  /**
   * 在扇形中显示弧形文字
   * @param {Object} position - 位置坐标 {x, y}
   * @param {number} angle - 文字角度
   * @param {string} text - 显示文字
   * @param {boolean} shouldFlip - 是否翻转文字
   * @param {string} [fillStyle] - 文字颜色
   * @param {string} [font] - 文字字体
   */
  drawHouseLabel (position, angle, text, shouldFlip, fillStyle, font) {
    angle = CanvasUtils.normalizeAngle(angle)

    this.ctx.save()
    this.ctx.translate(position.x, position.y)

    if (shouldFlip) {
      angle = CanvasUtils.normalizeAngle(angle + 180)
    }

    this.ctx.rotate(CanvasUtils.degToRad(angle - 90))
    this.ctx.font = font
    this.ctx.fillStyle = fillStyle
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'
    this.ctx.fillText(text, 0, 0)
    this.ctx.restore()
  }

  /**
   * 绘制圆环径线
   * @param {number} angle - 角度
   * @param {number} [innerRadiusRatio] - 内半径比例
   * @param {number} [outerRadiusRatio] - 外半径比例
   * @param {string} [strokeStyle] - 描边颜色
   * @param {number} [lineWidth] - 线宽
   */
  drawRadialLine (angle, innerRadiusRatio, outerRadiusRatio, strokeStyle, lineWidth) {
    angle = CanvasUtils.normalizeAngle(angle)

    const innerRadius = this.radius * (innerRadiusRatio)
    const outerRadius = this.radius * (outerRadiusRatio)

    const start = CanvasUtils.getPoint(angle, innerRadius, this.center, this.zeroAngle)
    const end = CanvasUtils.getPoint(angle, outerRadius, this.center, this.zeroAngle)

    this.ctx.beginPath()
    this.ctx.moveTo(start.x, start.y)
    this.ctx.lineTo(end.x, end.y)
    this.ctx.strokeStyle = strokeStyle
    this.ctx.lineWidth = lineWidth
    this.ctx.stroke()
  }

  /**
   * 在圆上显示径向文字
   * @param {number} angle - 角度
   * @param {number} distanceRatio - 距离比例
   * @param {string} text - 显示文字
   * @param {string} [fillStyle] - 文字颜色
   * @param {string} [font] - 文字字体
   * @returns {Object} 坐标点 {x, y}
   */
  drawRadialText (angle, distanceRatio, text, fillStyle, font) {
    if (!text) return this.center
    angle = CanvasUtils.normalizeAngle(angle)

    const distance = this.radius * distanceRatio
    const pos = CanvasUtils.getPoint(angle, distance, this.center, this.zeroAngle)

    this.ctx.font = font
    this.ctx.fillStyle = fillStyle
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'
    this.ctx.fillText(text, pos.x, pos.y)

    return pos
  }

  // 在类中添加这个方法
  /**
 * 沿半径方向绘制文字（从圆心向外辐射排列）
 * @param {number} angle - 文字所在角度
 * @param {number} startDistanceRatio - 起始位置距离圆心的比例
 * @param {number} spacing - 字符间距（像素）
 * @param {string} text - 要绘制的文本
 * @param {Object} [options] - 配置选项
 * @param {string} [options.fillStyle] - 文字颜色
 * @param {string} [options.font] - 字体样式
 * @returns {Object} 中心点坐标 {x, y}
 */
  drawTextAlongRadius (angle, startDistanceRatio, spacing, text, options = {}) {
    const {
      fillStyle = '#000',
      font = '16px 楷体'
    } = options

    // 保存当前画布状态
    this.ctx.save()

    // 设置文本样式
    this.ctx.fillStyle = fillStyle
    this.ctx.font = font
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'

    // 转换为弧度
    const radian = CanvasUtils.degToRad(angle, this.zeroAngle)

    // 起始位置距离
    const startDistance = this.radius * startDistanceRatio

    // 计算中心点位置（用于返回）
    let centerDistance = startDistance
    if (angle > 90 && angle < 270) {
      centerDistance = startDistance - ((text.length - 1) * spacing) / 2
    } else {
      centerDistance = startDistance - ((text.length - 1) * spacing) / 2
    }
    const centerX = this.center.x + Math.cos(radian) * centerDistance
    const centerY = this.center.y + Math.sin(radian) * centerDistance

    // 绘制每个字符
    for (let i = 0; i < text.length; i++) {
      // 计算每个字符位置
      const distance = startDistance - i * spacing

      const x = this.center.x + Math.cos(radian) * distance
      const y = this.center.y + Math.sin(radian) * distance

      // 保存当前状态
      this.ctx.save()
      this.ctx.translate(x, y)

      // 绘制字符
      this.ctx.fillText(text[i], 0, 0)

      // 恢复状态
      this.ctx.restore()
    }

    // 恢复原始画布状态
    this.ctx.restore()

    // 返回中心点坐标
    return { x: centerX, y: centerY }
  }

  /**
   * 绘制轴线
   * @param {number} directionAngle - 方向角度
   * @param {string} [strokeStyle] - 轴线颜色
   * @param {number} [lineWidth] - 线宽
   * @param {number} [outerRadiusRatio] - 外半径比例
   * @param {number} [innerRadiusRatio] - 内半径比例
   * @param {boolean} [drawOppositeDirection=true] - 是否同时绘制对宫方向的半轴
   * @returns {Object} 轴线端点坐标 {outer: {x, y}, inner: {x, y}}
   */
  drawAxisArrow (directionAngle, strokeStyle, lineWidth, outerRadiusRatio, innerRadiusRatio, drawOppositeDirection = true) {
    directionAngle = CanvasUtils.normalizeAngle(directionAngle)

    const startAngle = directionAngle + 180
    const outerRadiusRatioVal = outerRadiusRatio
    const innerRadiusRatioVal = innerRadiusRatio

    const startPoint = CanvasUtils.getPoint(
      startAngle,
      this.radius * outerRadiusRatioVal,
      this.center,
      this.zeroAngle
    )

    const endPoint = CanvasUtils.getPoint(
      directionAngle,
      this.radius * outerRadiusRatioVal,
      this.center,
      this.zeroAngle
    )

    const midEndPoint = CanvasUtils.getPoint(
      startAngle,
      this.radius * innerRadiusRatioVal,
      this.center,
      this.zeroAngle
    )

    const midStartPoint = CanvasUtils.getPoint(
      directionAngle,
      this.radius * innerRadiusRatioVal,
      this.center,
      this.zeroAngle
    )

    this.ctx.beginPath()
    if (drawOppositeDirection) {
      this.ctx.moveTo(startPoint.x, startPoint.y)
      this.ctx.lineTo(midEndPoint.x, midEndPoint.y)
    }
    this.ctx.moveTo(midStartPoint.x, midStartPoint.y)
    this.ctx.lineTo(endPoint.x, endPoint.y)
    this.ctx.strokeStyle = strokeStyle
    this.ctx.lineWidth = lineWidth
    this.ctx.stroke()

    // 返回当前可见半轴的中点位置信息（用于悬停检测）
    return {
      center: {
        x: (endPoint.x + midStartPoint.x) / 2,
        y: (endPoint.y + midStartPoint.y) / 2
      }
    }
  }

  /**
 * 在极坐标系中绘制星号标记
 * @param {number} angle - 星号位置的角度（弧度制）
 * @param {number} distanceRatio - 距离中心点的比例（0-1）
 * @param {string} [asterisk='*'] - 星号字符（默认'*'，支持自定义如'★'）
 * @param {string} [asteriskFillStyle='black'] - 星号颜色
 * @param {string} [asteriskFont='20px Arial'] - 星号字体样式
 * @returns {Object} 星号绘制的坐标点 {x, y}
 */
  drawAsterisk (angle, distanceRatio, asterisk = '*', asteriskFillStyle = 'gold', asteriskFont = '20px Arial') {
    // 标准化角度确保在0-2π范围内
    angle = CanvasUtils.normalizeAngle(angle)

    // 计算星号坐标（基于极坐标转换）
    const distance = this.radius * distanceRatio
    const pos = CanvasUtils.getPoint(angle, distance, this.center, this.zeroAngle)

    // 保存画布状态避免污染
    this.ctx.save()

    // 配置星号样式
    this.ctx.font = asteriskFont
    this.ctx.fillStyle = asteriskFillStyle
    this.ctx.textAlign = 'center' // 水平居中
    const metrics = this.ctx.measureText(asterisk)
    const ascent = metrics.actualBoundingBoxAscent // 基线上方高度
    const descent = metrics.actualBoundingBoxDescent // 基线下方的深度

    const verticalCenter = pos.y - (descent - ascent) / 2
    // 绘制星号字符
    this.ctx.fillText(asterisk, pos.x, verticalCenter)

    // 恢复原始画布状态
    this.ctx.restore()

    return { x: pos.x, y: verticalCenter }
  }

  /**
   * 绘制带文字标注的圆圈
   * @param {number} angle - 角度
   * @param {number} distanceRatio - 距离比例
   * @param {string} [fillStyle] - 圆圈填充色
   * @param {number} [circleRadius] - 圆圈半径
   * @param {string} [label] - 标签文字
   * @param {string} [labelFillStyle] - 标签颜色
   * @param {string} [labelFont] - 标签字体
   * @returns {Object} 坐标点 {x, y}
   */
  drawLabeledCircle (angle, distanceRatio, fillStyle, circleRadius, label, labelFillStyle, labelFont) {
    angle = CanvasUtils.normalizeAngle(angle)

    const distance = this.radius * distanceRatio
    const pos = CanvasUtils.getPoint(angle, distance, this.center, this.zeroAngle)

    // 绘制圆点
    this.ctx.beginPath()
    this.ctx.arc(pos.x, pos.y, circleRadius, 0, Math.PI * 2)
    this.ctx.fillStyle = fillStyle
    this.ctx.fill()

    // 绘制标签
    if (label) {
      this.ctx.font = labelFont
      this.ctx.fillStyle = labelFillStyle
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'top'
      this.ctx.fillText(label, pos.x, pos.y + (circleRadius) + 5)
    }

    return pos
  }

  /**
   * 绘制行星连线
   * @param {Object} startPos - 起点坐标 {x, y}
   * @param {Object} endPos - 终点坐标 {x, y}
   * @param {string} [strokeStyle] - 连线颜色
   * @param {number} [lineWidth] - 线宽
   */
  drawPlanetConnection (startPos, endPos, strokeStyle, lineWidth) {
    this.ctx.beginPath()
    this.ctx.moveTo(startPos.x, startPos.y)
    this.ctx.lineTo(endPos.x, endPos.y)
    this.ctx.strokeStyle = strokeStyle
    this.ctx.lineWidth = lineWidth
    this.ctx.stroke()
  }
}
